import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import actionWebInvoke from '../utils/actionCallUtils';
import getHeaders from '../utils/headers';
import actions from '../config.json';
import OBJCODE from '../constants';

const SearchContext = createContext({});
function NavbarSearchProvider({ children, runtime, ims }) {
  const [stockResult, setStockResult] = useState([]);
  const [ccResult, setCCResult] = useState([]);
  const [aemResult, setAemResult] = useState([]);
  const [projectResult, setProjectResult] = useState([]);
  const [taskResult, setTaskResult] = useState([]);
  const [status, setStatus] = useState();
  const [isLoading, setLoading] = useState(false);

  async function getStockImages(searchText) {
    const queryParams = `search_parameters[words]=${searchText}`;
    const actionResponse = await actionWebInvoke(
      actions['adobe-stock-search-action'],
      {},
      { queryParams },
    );

    if (actionResponse) {
      setLoading(false);
      setStockResult({
        ...stockResult,
        stockImageResponse: actionResponse.files,
        stockSearchText: searchText,
      });
    }
  }

  async function getCCImages(searchText) {
    const headers = {
      'x-api-key': '1f4fc870c9e9423b8d45a40e8fecffcc',
      Authorization: `Bearer ${ims.token}`,
    };
    const actionResponse = await actionWebInvoke(
      actions['creative-cloud-search'],
      headers,
      { searchText },
    );
    if (actionResponse) {
      setLoading(false);
      setCCResult({
        ...ccResult,
        ccImageResponse: actionResponse,
        ccSearchText: searchText,
      });
    }
  }

  async function getAemImages(searchText) {
    const headers = getHeaders(ims);
    const queryParams = `fulltext=${searchText}&path=/content/dam&type=dam:Asset&configid=ims`;
    try {
      const actionResponse = await actionWebInvoke(
        actions['aem-assets-search'],
        headers,
        { queryParams },
      );
      if (actionResponse) {
        setLoading(false);
        setAemResult({
          ...aemResult,
          aemImageResponse: actionResponse,
          aemSearchText: searchText,
          isLoading: false,
        });
      }
    } catch (error) {
      console.log('error occurred in searchcontext', error);
      setAemResult({ ...aemResult, searchText, isLoading: false });
    }
  }

  async function getWFProjects(searchText) {
    const fields = [
      'program:*',
      'portfolio:*',
      'plannedStartDate',
      'plannedCompletionDate',
      'status',
      'percentComplete',
      'owner:*',
    ];

    const queryParams = {
      name: searchText,
      name_Mod: 'cicontains',
      plannedCompletionDate_Sort: 'asc',
    };

    const projSearchResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.PROJECT_SEARCH, queryParams, fields },
    );
    if (projSearchResponse) {
      setLoading(false);
      setProjectResult({
        ...projectResult,
        projectResponse: projSearchResponse,
        projectSearchText: searchText,
      });
    }
  }

  async function getWFTasks(searchText) {
    const fields = [
      'description',
      'project:program',
      'project:portfolio',
      'plannedStartDate',
      'plannedCompletionDate',
      'status',
      'percentComplete',
      'assignmentsListString',
      'assignments:assignedTo',
    ];

    const queryParams = {
      name: searchText,
      name_Mod: 'cicontains',
      plannedCompletionDate_Sort: 'asc',
    };

    const taskSearchResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.TASK_SEARCH, queryParams, fields },
    );
    if (taskSearchResponse) {
      setLoading(false);
      setTaskResult({
        ...taskResult,
        taskResponse: taskSearchResponse,
        taskSearchText: searchText,
      });
    }
  }

  const onProjectsSearch = searchText => {
    setLoading(true);
    setStatus('projectsSearch');
    getWFProjects(searchText);
  };

  const onTasksSearch = searchText => {
    setLoading(true);
    setStatus('tasksSearch');
    getWFTasks(searchText);
  };

  const onSubmit = searchText => {
    setLoading(true);
    setStatus('stockImage');
    getStockImages(searchText);
  };

  const onCCSubmit = searchText => {
    setLoading(true);
    setStatus('ccImage');
    getCCImages(searchText);
  };

  const onAemSubmit = searchText => {
    setLoading(true);
    setStatus('aemImage');
    getAemImages(searchText);
  };

  return (
    <SearchContext.Provider
      value={{
        onProjectsSearch,
        onTasksSearch,
        onSubmit,
        onCCSubmit,
        onAemSubmit,
        stockResult,
        aemResult,
        ccResult,
        projectResult,
        taskResult,
        runtime,
        ims,
        status,
        isLoading,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

function searchProvider() {
  const context = useContext(SearchContext);
  return context;
}

NavbarSearchProvider.propTypes = {
  ims: PropTypes.shape({
    token: PropTypes.string,
    org: PropTypes.string,
  }),
  runtime: PropTypes.shape({}),
  children: PropTypes.arrayOf(PropTypes.shape({})),
};

NavbarSearchProvider.defaultProps = {
  ims: {},
  runtime: {},
  children: {},
};

export { NavbarSearchProvider, searchProvider };
